import * as yup from "yup";
import { FieldErrors } from "react-hook-form";
import { SaleItem } from "../../models/saleItem";
import { FullProperty } from "../../models/fullProperty";
import { ImageService } from "../../services/images/images";
import { StringsService } from "../../services/strings/strings";
import { v4 as uuidv4 } from "uuid";
import { restaurantService } from "../../services/firebase/restaurantManagementService";
import { toast } from "react-toastify";

export interface SaleItemFormData {
    name: string;
    description: string;
    originalPrice: number;
    discountPrice: number;
    isActive: boolean;
    available: number;
    startOfCollectionTime: string;
    endDate: string;
    foodCategory: string;
    discountReason: string;
};

export const saleItemSchema = (restaurant: FullProperty) => yup.object({
    name: yup.string().required("Name is required"),
    available: yup.number().required("Availability is required"),
    isActive: yup.boolean().required("Active status is required"),
    originalPrice: yup
        .number()
        .positive("Price must be greater than 0")
        .required("Price is required"),
    discountPrice: yup
        .number()
        .positive("Price must be greater than 0")
        .required("Price is required")
        .test(
            "is-less-than-original",
            "Discount price must be less than the original price",
            function (value) {
                const { originalPrice } = this.parent;
                return value < originalPrice;
            }
        ),
    foodCategory: yup
        .string()
        .required("Food category is required"),
    discountReason: yup
        .string()
        .required("Discount reason is required"),
    description: yup.string().required("Description required"),
    startOfCollectionTime: yup
        .string()
        .required("Collection start time is required")
        .test("is-future", "Collection start time must be in the future", (value) => {
            const now = new Date();
            return new Date(value) > now;
        })
        .test("is-future", "Collection start time must be before End date", function (value) {
            const { endDate } = this.parent;
            return new Date(value) < new Date(endDate);
        }),
    endDate: yup
        .string()
        .required("End date is required")
        .test("is-future", "End date must be in the future", (value) => {
            const now = new Date();
            return new Date(value) > now;
        })
        .test(
            "within-48-hours",
            "End date must be before your restaurant closes tomorrow",
            (value) => {
                const closingTime = new Date(restaurant.closingTime);
                const maxDate = new Date(); // start with today
                maxDate.setDate(maxDate.getDate() + 1); // move to tomorrow

                // Now set maxDate's time to the restaurant's closing time
                maxDate.setHours(closingTime.getHours(), closingTime.getMinutes(), 0, 0);

                return new Date(value) <= maxDate;
            }
        )
});

export const onSubmitError = (errors: FieldErrors, fieldRefs: Record<string, React.RefObject<HTMLDivElement>>) => {
    const firstErrorKey = Object.keys(errors)[0] as keyof typeof fieldRefs;
    const ref = fieldRefs[firstErrorKey];

    if (ref?.current) {
        ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
        ref.current.focus?.();
    }
};

export const getDaysUntil = (until: string): number => {
    const start = new Date();
    const end = new Date(until);

    // Reset both to midnight to ignore time-of-day
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    const MS_PER_DAY = 1000 * 60 * 60 * 24;
    return Math.round((end.getTime() - start.getTime()) / MS_PER_DAY);
}

export const upsertStrings = async (id: string, saleItemId: string, data: SaleItemFormData) => {
    await StringsService.updateStringById(
        `restaurant-${id}-saleItem-${saleItemId}-sale-item-name`,
        data.name
    )
    await StringsService.updateStringById(
        `restaurant-${id}-saleItem-${saleItemId}-sale-item-description`,
        data.description
    )
}

export const handleSaveItem = async (
    data: SaleItemFormData,
    restaurant: FullProperty,
    currentItem: SaleItem,
    editingItem: boolean,
    saleItems: SaleItem[],
    setSaleItems: React.Dispatch<React.SetStateAction<SaleItem[]>>,
    thumbnailImage: File | null,
    setLoading: React.Dispatch<React.SetStateAction<boolean>>,
    onSuccess: () => void
) => {
    if (!restaurant.id || !currentItem) return;
    setLoading(true);
    try {
        currentItem.originalPrice = Math.floor(data.originalPrice);
        currentItem.discountPrice = Math.floor(data.discountPrice);
        currentItem.isActive = data.isActive;
        currentItem.available = data.available;
        currentItem.startOfCollectionTimeDayDeltaFromNow = getDaysUntil(data.startOfCollectionTime)
        currentItem.endDateDayDeltaFromNow = getDaysUntil(data.endDate)
        currentItem.startOfCollectionTime = data.startOfCollectionTime;
        currentItem.endDate = data.endDate;
        currentItem.foodCategory = data.foodCategory;
        currentItem.discountReason = data.discountReason;

        if (editingItem) {
            currentItem.originalPrice = Math.floor(currentItem.originalPrice);
            currentItem.discountPrice = Math.floor(currentItem.discountPrice);
            const newSaleItems = saleItems.map((item) => (item.id === currentItem.id ? currentItem : item));
            await restaurantService.editRestaurant(restaurant.id, { saleItems: newSaleItems });
            setSaleItems(newSaleItems)
        } else {
            currentItem.id = uuidv4();
            currentItem.originalPrice = Math.floor(currentItem.originalPrice);
            currentItem.discountPrice = Math.floor(currentItem.discountPrice);
            currentItem.createdOn = new Date().toISOString();
            await restaurantService.addSaleItem(restaurant.id, currentItem);
            setSaleItems((prev) => [...prev, currentItem]);
        }

        if (thumbnailImage) {
            const thumbnailUrl: string = (await ImageService.uploadImagesToStorage([thumbnailImage]))[0];
            await StringsService.updateStringById(
                `restaurant-${restaurant.id}-sale-item-${currentItem.id}-thumbnail`,
                thumbnailUrl
            )
        }
        await upsertStrings(restaurant.id, currentItem.id, data)
        setLoading(false);
        onSuccess();
        toast.success('Sales item updated successfully!', {
            position: 'top-right',
            autoClose: 5000,
        });
    } catch (error) {
        console.log(error)
        setLoading(false);
        toast.error("Failed to save sale item.");
    }
};

export function updateTime(
    originalTimestamp: string | Date,
    dayDelta?: number
): Date {
    const today = new Date();
    const original = new Date(originalTimestamp);

    const result = new Date(today);
    if (typeof dayDelta === "number") {
        result.setDate(result.getDate() + dayDelta);
    }
    result.setHours(
        original.getHours(),
        original.getMinutes(),
        original.getSeconds(),
        original.getMilliseconds()
    );

    return result;
}
